import {observer} from 'mobx-react-lite'
import React, {useLayoutEffect, useRef} from 'react'
import tw from 'tailwind-styled-components'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {useScrollFade} from '@components/main-menu/menu-items/use-scroll-fade'
import {useForceUpdate, useSyncEvent} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {GroupChatProxy} from '@extensions/proxies/group-chat-proxy'
import {PersonalChatProxy} from '@extensions/proxies/personal-chat-proxy'
import {useMainMenuStore} from '../main-menu-store'
import {CreateGroupChatButton} from './chats/create-group-chat-button'
import {GroupProjectListItem} from './chats/group-project-list-item'
import {PersonalProjectListItem} from './chats/personal-project-list-item'

const TwChatsList = tw(WithPerfectScrollbar)<{$open?: boolean}>`
  select-none
  flex
  flex-grow
  flex-shrink
  flex-col
  cursor-pointer
  -mr-8
  pr-8
  mb-12

  perfect-scrollbar-with-custom-scroll
`

export const getFlexBasis = (items: unknown[]) => {
  const fb = Math.min(items.length * 30, 320)
  return `${fb}px`
}

export const ChatsList = observer(({open}: {open?: boolean}) => {
  const [state] = useMainMenuStore()
  const forceUpdate = useForceUpdate()
  useSyncEvent(
    'profile',
    {debounceTime: 50, filter: globals.currentUserId},
    () => {
      forceUpdate()
    }
  )

  const containerRef = useRef<HTMLElement>()
  const {refreshFade} = useScrollFade(containerRef)
  useLayoutEffect(() => {
    refreshFade()
  }, [open, refreshFade])

  if (!open) {
    return <div className="flex flex-shrink flex-grow basis-4"></div>
  }

  const {usersAndGroups, canCreateGroupChat} = state

  const sortedUsers = [...usersAndGroups].sort((a, b) => {
    const imp = Number(b.markImportant || 0) - Number(a.markImportant || 0)
    if (imp !== 0) {
      return imp
    }

    return b.newMessagesAmount - a.newMessagesAmount
  })

  const chatsFlexBasis = getFlexBasis(sortedUsers)

  const flexBasis = open ? chatsFlexBasis : 'auto'
  const cls = sortedUsers.length <= 1 ? 'min-h-28' : 'mih-h-56'

  return (
    <TwChatsList
      containerRef={el => (containerRef.current = el)}
      style={{flexBasis}}
      $open={open}
      className={cls}
      onScroll={refreshFade}
    >
      {canCreateGroupChat && <CreateGroupChatButton />}
      {sortedUsers.map((u: PersonalChatProxy | GroupChatProxy) =>
        'users' in u ? (
          <GroupProjectListItem u={u} key={u.chatId} />
        ) : (
          <PersonalProjectListItem u={u} key={u.chatId} />
        )
      )}
    </TwChatsList>
  )
})
